import React, { useState, useEffect } from 'react';
import { paymentTypesApi } from '../lib/api';
import { CreditCard, Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const PaymentTypesPage = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', is_cash_payment: false, position: 0 });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await paymentTypesApi.list(); setTypes(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await paymentTypesApi.create(form);
      toast.success('Payment type created');
      setShowModal(false);
      setForm({ name: '', description: '', is_cash_payment: false, position: 0 });
      load();
    } catch (e) { toast.error('Failed to create payment type'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment type?')) return;
    try { await paymentTypesApi.delete(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="payment-types-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Organization</span><span>/</span><span className="text-[#1f1f1f] font-medium">Payment Types</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Payment Types</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-payment-type-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Payment Type
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Description</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Cash Payment</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Position</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            types.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">No payment types. Click "Add Payment Type" to create one.</td></tr> :
            types.map(t => (
              <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`pt-row-${t.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{t.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{t.description || '-'}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${t.is_cash_payment ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.is_cash_payment ? 'Yes' : 'No'}</span></td>
                <td className="px-4 py-3 text-sm text-gray-600">{t.position}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="pt-form-desc">
          <DialogHeader><DialogTitle>Add Payment Type</DialogTitle></DialogHeader>
          <p id="pt-form-desc" className="sr-only">Form to add payment type</p>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="pt-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pt-desc" /></div>
            <div><Label>Position</Label><Input type="number" value={form.position} onChange={e => setForm({...form, position: parseInt(e.target.value) || 0})} data-testid="pt-position" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_cash_payment} onCheckedChange={v => setForm({...form, is_cash_payment: v})} data-testid="pt-cash" /><Label>Is Cash Payment</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="pt-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentTypesPage;
